import { useCallback, useMemo, useState } from "react";

const useListProcessing = (data, config) => {
  const [filters, setFilters] = useState({});
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [order, setOrder] = useState(null);

  const setFilter = useCallback(
    (filterKey, filterValue) => {
      if (filterKey in (config?.filter ?? {})) {
        setFilters((prevFilters) => ({
          ...prevFilters,
          [filterKey]: filterValue,
        }));
      }
    },
    [config]
  );

  const getFilter = useCallback((filterKey) => {
    return filters[filterKey];
  }, [filters]);

  const filteredData = useMemo(() => {
    const result = Object.entries(filters).reduce(
      (acc, [filterKey, filterValue]) => {
        if (filterKey in (config?.filter ?? {})) {
          return acc.filter((item) => {
            return config.filter[filterKey](item, filterValue);
          });
        } else {
          return acc;
        }
      },
      data ?? []
    );
    return result;
  }, [data, filters, config]);

  const sortedData = useMemo(() => {
    if (order === null && !config?.order?._DEFAULT) {
      return filteredData;
    }

    const orderType = order?.type ?? config?.order?._DEFAULT?.type;
    const orderFunction = config?.order?.[orderType];
    const orderAscending = order?.ascending ?? config?.order?._DEFAULT?.ascending;

    if (typeof orderFunction !== 'function') {
      return filteredData;
    }

    if (orderAscending) {
      return filteredData.sort((a, b) => orderFunction(a, b));
    } else {
      return filteredData.sort((a, b) => orderFunction(b, a));
    }
  }, [filteredData, order, config]);

  const totalPages = useMemo(() => {
    if (sortedData.length === 0) return 0;
    return Math.ceil(sortedData.length / (config?.pagination?.pageSize ?? sortedData.length));
  }, [sortedData, config]);

  const paginatedData = useMemo(() => {
    if (sortedData.length === 0) return sortedData;
    const pageSize = config?.pagination?.pageSize ?? sortedData.length;
    const start = currentPageIndex * pageSize;
    const end = start + pageSize;
    return sortedData.slice(start, end);
  }, [sortedData, currentPageIndex, config]);

  return {
    processedData: paginatedData,
    setFilter: setFilter,
    getFilter: getFilter,
    totalCount: sortedData.length,
    totalPages: totalPages,
    currentPageIndex: currentPageIndex,
    setCurrentPageIndex: setCurrentPageIndex,
  };
};

export default useListProcessing;
